import { useState, useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Factory,
  Sparkles,
  Target,
  TrendingUp,
  Wrench,
  Zap,
} from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { buildAuthorSchema, buildPublisherSchema } from '@/lib/authorSchema'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'
import { caseStudies, industries, type CaseStudy } from '@/data/caseStudies'

function CaseStudyCard({ study }: { study: CaseStudy }) {
  return (
    <Link
      to={`/case-studies/${study.slug}`}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 overflow-hidden"
    >
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-4">
          <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
            {study.industry}
          </span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
          {study.headline}
        </h3>

        <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
          {study.summary}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <div className="text-2xl font-bold text-blue-600">{study.keyMetric.value}</div>
            <div className="text-xs text-gray-500">{study.keyMetric.label}</div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
            Read story
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  )
}

export function CaseStudiesList() {
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null)

  useSEO({
    title: 'Customer Success Stories | Algoscale',
    description: 'Explore real-world AI document processing case studies. See how Algoscale automates document extraction, improves accuracy, and boosts enterprise efficiency.',
    canonical: 'https://documentiq.algoscale.com/case-studies',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'DocumentIQ Case Studies',
        description: 'Real-world case studies showing how teams use DocumentIQ.',
        url: 'https://documentiq.algoscale.com/case-studies',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: caseStudies.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://documentiq.algoscale.com/case-studies/${c.slug}`,
            name: c.headline,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Case Studies', item: 'https://documentiq.algoscale.com/case-studies' },
        ],
      },
    ],
  })

  const filtered = activeIndustry
    ? caseStudies.filter((c) => c.industry === activeIndustry)
    : caseStudies

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-6">
            <TrendingUp size={14} />
            Real Results
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Case Studies
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            See how teams use DocumentIQ to extract structured data from
            thousands of documents — accurately and at scale.
          </p>
        </div>
      </section>

      {/* Industry Filter */}
      <section className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-4 overflow-x-auto">
            <button
              onClick={() => setActiveIndustry(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeIndustry === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Industries
            </button>
            {industries.map((industry) => (
              <button
                key={industry}
                onClick={() => setActiveIndustry(activeIndustry === industry ? null : industry)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeIndustry === industry
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {industry}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Grid */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <p>No case studies for this industry yet.</p>
              <button
                onClick={() => setActiveIndustry(null)}
                className="mt-3 text-blue-600 text-sm font-medium hover:underline"
              >
                View all case studies
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filtered.map((study) => (
                <CaseStudyCard key={study.slug} study={study} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Have a similar use case?
          </h2>
          <p className="text-gray-600 mb-8">
            Start extracting structured data from your documents in minutes — no setup fee, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 text-sm"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  )
}

export function CaseStudyDetail() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const study = caseStudies.find((c) => c.slug === slug)

  const jsonLd = useMemo(() => {
    if (!study) return undefined
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: study.headline,
        description: study.summary,
        articleSection: 'Case Studies',
        keywords: `${study.industry}, document extraction, case study, ${study.companyType}`,
        about: {
          '@type': 'Thing',
          name: study.industry,
        },
        mentions: study.featuresUsed.map((f) => ({ '@type': 'Thing', name: f })),
        author: buildAuthorSchema('Algoscale'),
        publisher: buildPublisherSchema(),
        mainEntityOfPage: `https://documentiq.algoscale.com/case-studies/${study.slug}`,
        image: `https://documentiq.algoscale.com/og/case-study/${study.slug}.png`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Case Studies', item: 'https://documentiq.algoscale.com/case-studies' },
          { '@type': 'ListItem', position: 3, name: study.industry, item: `https://documentiq.algoscale.com/case-studies/${study.slug}` },
        ],
      },
    ]
  }, [study])

  useSEO({
    title: study?.headline || 'Case Study',
    description: study?.summary || 'DocumentIQ case study',
    canonical: study ? `https://documentiq.algoscale.com/case-studies/${study.slug}` : undefined,
    ogType: 'article',
    ogImage: study ? `/og/case-study/${study.slug}.png` : undefined,
    jsonLd,
  })

  if (!study) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Case study not found.</p>
          <Link to="/case-studies" className="text-blue-600 font-medium hover:underline">
            View all case studies
          </Link>
        </div>
      </div>
    )
  }

  const currentIndex = caseStudies.indexOf(study)
  const nextStudy = caseStudies[(currentIndex + 1) % caseStudies.length]

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/case-studies" className="hover:text-gray-900 transition-colors flex items-center gap-1">
              <ArrowLeft size={14} />
              Case Studies
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{study.industry}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
            {study.industry}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-4">
            {study.headline}
          </h1>
          <p className="text-lg text-gray-600">
            {study.companyType}
          </p>
        </div>
      </section>

      {/* Key Metric Banner */}
      <section className="border-y border-gray-200 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-12">
            <div className="text-center sm:text-left">
              <div className="text-4xl sm:text-5xl font-bold text-blue-600">{study.keyMetric.value}</div>
              <div className="text-sm text-gray-600 mt-1">{study.keyMetric.label}</div>
            </div>
            <div className="h-px sm:h-12 w-full sm:w-px bg-blue-200" />
            <p className="text-gray-700 text-sm leading-relaxed flex-1">
              {study.summary}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Challenge */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                <Target size={18} className="text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">The Challenge</h2>
            </div>
            <p className="text-gray-600 leading-relaxed pl-[46px]">
              {study.challenge}
            </p>
          </div>

          {/* Solution */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <Wrench size={18} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">The Solution</h2>
            </div>
            <p className="text-gray-600 leading-relaxed pl-[46px]">
              {study.solution}
            </p>
          </div>

          {/* Results */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                <TrendingUp size={18} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">The Results</h2>
            </div>
            <ul className="space-y-3 pl-[46px]">
              {study.results.map((result, i) => (
                <li key={i} className="flex items-start gap-2.5 text-gray-600">
                  <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{result}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Features Used */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <Zap size={18} className="text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">DocumentIQ Features Used</h2>
            </div>
            <div className="flex flex-wrap gap-2 pl-[46px]">
              {study.featuresUsed.map((feature) => (
                <span
                  key={feature}
                  className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-sm text-gray-700"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Next Case Study */}
      {nextStudy && nextStudy.slug !== study.slug && (
        <section className="border-t border-gray-200 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="text-sm text-gray-500 mb-3">Next case study</p>
            <Link
              to={`/case-studies/${nextStudy.slug}`}
              className="group flex items-center justify-between"
            >
              <div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                  {nextStudy.industry}
                </span>
                <h3 className="text-lg font-semibold text-gray-900 mt-2 group-hover:text-blue-600 transition-colors">
                  {nextStudy.headline}
                </h3>
              </div>
              <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 ml-4" />
            </Link>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to see similar results?
          </h2>
          <p className="text-blue-100 mb-8">
            Start extracting structured data from your documents today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-white font-medium hover:bg-blue-500/20 transition-colors text-sm"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  )
}
